import React from 'react'
import { Autocomplete, Button, Chip, TextField } from '@mui/material'
import { PuntoColor, fmt } from './columnasPrecios'
import MoneyField from './MoneyField'
import { precioConBono } from './bonosUtils'

// Para buscar sin importar tildes ni mayúsculas («popayan» encuentra «POPAYAN (CAUCA)»).
const normal = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()

/**
 * Cobertura de un vehículo: 'Nacional' o departamentos y/o ciudades (F104/F106: «VALLE DEL CAUCA»,
 * «CALI (VALLE DEL CAUCA)»). Se guarda como texto ('Nacional' / 'VALLE DEL CAUCA, PASTO (NARIÑO)').
 * Nacional y lo demás se excluyen: elegir uno quita lo otro. Lo ya guardado con zonas se sigue viendo.
 * grupoDe (opcional): el grupo de cada opción para listarlas por secciones (Nacional / Departamentos / Ciudades).
 */
const SelectorCobertura = ({ value, onChange, opciones = [], grupoDe = null, disabled = false, label = 'Cobertura', sx = {} }) => {
  const sel = String(value || '').split(', ').map((s) => s.trim()).filter(Boolean)
  return (
    <Autocomplete
      multiple
      size="small"
      options={opciones}
      value={sel}
      disabled={disabled}
      {...(grupoDe ? { groupBy: grupoDe } : {})}
      // ~1.100 ciudades: Nacional siempre primero y máximo 150 resultados para que no se trabe.
      filterOptions={(opts, { inputValue }) => {
        const q = normal(inputValue).trim()
        const lista = q ? opts.filter((o) => normal(o).includes(q)) : opts
        return lista.slice(0, 150)
      }}
      onChange={(_, v) => {
        const ultimo = v[v.length - 1]
        const limpio = ultimo === 'Nacional' ? ['Nacional'] : v.filter((x) => x !== 'Nacional')
        onChange(limpio.join(', '))
      }}
      renderTags={(value2, getTagProps) => value2.map((o, i) => (
        <Chip size="small" label={o} {...getTagProps({ index: i })} key={o} />
      ))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!sel.length && !disabled}
          placeholder={sel.length ? '' : 'Nacional o busca un departamento o ciudad'}
          helperText={!sel.length && !disabled ? 'Elige Nacional o los departamentos / ciudades donde aplica.' : ''}
        />
      )}
      sx={sx}
    />
  )
}

/** Elegir un archivo de imagen y entregarlo; el input se limpia para poder elegir el mismo otra vez. */
const BotonArchivo = ({ texto, onArchivo, size = 'small', variant = 'outlined' }) => (
  <Button size={size} variant={variant} component="label">
    {texto}
    <input
      type="file"
      accept="image/*"
      hidden
      onChange={(e) => {
        const file = e.target.files && e.target.files[0]
        if (file && onArchivo) onArchivo(file)
        e.target.value = ''
      }}
    />
  </Button>
)

/** Aviso de que los colores de un vehículo traían valores distintos (datos viejos o de TP). */
const Mixto = ({ texto }) => (
  <div style={{ fontSize: 11.5, color: '#8a5a00', background: '#fff8e8', border: '1px solid #f0d089', borderRadius: 6, padding: '4px 8px', marginTop: 4 }}>
    {texto}
  </div>
)

/**
 * F72/F73: cobertura (ciudades), términos legales y pieza gráfica POR VEHÍCULO (marca + modelo + año).
 * Un vehículo con varios colores lleva UNO de cada uno; otro vehículo del mismo bono, los suyos.
 * Solo pinta: el estado lo lleva el formulario del bono, que lo guarda junto con el resto.
 *
 * filas (vehículos): [{ material: clave, etiqueta, colores: [{material, color}], cobertura, legal,
 *                       img_url, mixto: { cobertura: [..]|null, legal: bool, img_url: bool }, cambiada }]
 * La cobertura es SOLO del vehículo (no se hereda del bono). Legal vacío = el que se genera con la
 * vigencia y la cobertura DE ESE vehículo (legalSugeridoDe).
 */
const CoberturaPorMoto = ({
  filas = [], opcionesCobertura = ['Nacional'], grupoCobertura = null, legalSugeridoDe = () => '', editable = false,
  onCambiar = null, onSubirPieza = null, pidePrecioAntes = false, efecto = '', monto = 0,
}) => {
  if (!filas.length) {
    return <div style={{ fontSize: 12.5, color: '#6b7684' }}>Primero elige los vehículos del bono (paso 1).</div>
  }

  const varios = filas.length > 1

  const tarjeta = (v, i) => {
    const mixto = v.mixto || {}
    const legalSugerido = legalSugeridoDe(v)
    return (
      <div
        key={v.material}
        style={{
          border: `1px solid ${v.cambiada ? '#a9dfc0' : '#dfe5ec'}`,
          borderLeft: `5px solid ${v.cambiada ? '#1f9d55' : '#2f5b8f'}`,
          borderRadius: 10, background: '#fff', marginBottom: 12, overflow: 'hidden',
        }}
      >
        {/* Encabezado: de qué vehículo es esta configuración, y sus colores */}
        <div style={{ background: v.cambiada ? '#f2fbf5' : '#f3f6fa', padding: '9px 14px', borderBottom: '1px solid #e3e7ec' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            {varios && (
              <span style={{ fontSize: 10.5, fontWeight: 700, color: '#fff', background: '#2f5b8f', borderRadius: 4, padding: '1px 7px' }}>
                Vehículo {i + 1} de {filas.length}
              </span>
            )}
            <b style={{ fontSize: 14, color: '#0f2439' }}>{v.etiqueta}</b>
            {v.cambiada && <span style={{ fontSize: 11, color: '#166534', fontWeight: 600 }}>· cambios sin guardar</span>}
            {/* F95: el precio de la moto y cómo queda con el bono: lo mismo que entrega la API. */}
            {(() => {
              // F99: una moto que aún no está en Lucia no tiene precio todavía; no es un error.
              if (String(v.material).startsWith('pend:')) {
                return <span style={{ fontSize: 11.5, color: '#8a5a00' }}>· entra sola al bono cuando llegue de SAP</span>
              }
              const pvp = Number(v.pvp_actual || 0)
              if (!pvp) return <span style={{ fontSize: 11.5, color: '#d64545' }}>· sin PVP en Lucia</span>
              const conBono = precioConBono({ precio: pvp, sap: v.pvp_sap, efecto, monto, estructural: pidePrecioAntes })
              return (
                <span style={{ fontSize: 11.5, color: '#56606b' }}>
                  · PVP {fmt(pvp)}
                  {conBono !== null && conBono !== pvp && <> · con el bono <b style={{ color: '#166534' }}>{fmt(conBono)}</b></>}
                </span>
              )
            })()}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            <span style={{ fontSize: 11.5, color: '#6b7684' }}>
              {(v.colores || []).length} color(es):
            </span>
            {(v.colores || []).map((c) => (
              <span
                key={c.material}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, background: '#fff',
                  border: '1px solid #e3e7ec', borderRadius: 12, padding: '1px 8px',
                }}
              >
                <PuntoColor nombre={c.color} />
                {c.color || 'sin color'}
                <span style={{ fontFamily: 'monospace', color: '#8a94a0' }}>{c.material}</span>
              </span>
            ))}
          </div>
        </div>

        {/* La configuración de ESTE vehículo */}
        <div style={{ padding: '12px 14px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 420px', minWidth: 300 }}>
            {pidePrecioAntes && (
              // F84: en precio estructural a SAP va el precio nuevo y el anterior se pierde. Este es el
              // que el sitio muestra tachado. Se sugiere el PVP vigente de la moto.
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <MoneyField
                  label="Precio antes (se muestra tachado)"
                  value={v.precio_antes}
                  disabled={!editable}
                  onChange={(val) => onCambiar && onCambiar(v.material, 'precio_antes', val)}
                  sx={{ width: 240 }}
                />
                {editable && !v.precio_antes && Number(v.pvp_actual) > 0 && (
                  <Button size="small" variant="text" onClick={() => onCambiar && onCambiar(v.material, 'precio_antes', String(v.pvp_actual))}>
                    Usar PVP vigente {fmt(v.pvp_actual)}
                  </Button>
                )}
                {!v.precio_antes && <span style={{ fontSize: 11.5, color: '#d64545' }}>Falta el precio antes.</span>}
                {(() => {
                  // F93: el precio de AHORA es el que ya tiene SAP (la baja es de estructura); el de antes,
                  // el que se escribe aquí. La diferencia es la baja real de este vehículo.
                  const antes = Number(String(v.precio_antes || '').replace(/\D/g, '')) || 0
                  const sap = Number(v.pvp_sap || 0)
                  if (!sap) {
                    return <span style={{ fontSize: 12.5, color: '#8a5a00' }}>Precio ahora (SAP): <b>sin precio en SAP todavía</b></span>
                  }
                  return (
                    <span style={{ fontSize: 12.5, color: '#0f2439' }}>
                      Precio ahora (SAP): <b>{fmt(sap)}</b>
                      {antes > 0 && (antes > sap
                        ? <span style={{ color: '#166534' }}> · baja {fmt(antes - sap)}</span>
                        : <span style={{ color: '#d64545' }}> · el precio antes debe ser mayor al de SAP</span>)}
                    </span>
                  )
                })()}
                {mixto.precio_antes && <Mixto texto="Sus colores tienen precios antes distintos. Si escribes uno, queda para todos." />}
              </div>
            )}
            <SelectorCobertura
              label="Cobertura de este vehículo"
              value={v.cobertura}
              opciones={opcionesCobertura}
              grupoDe={grupoCobertura}
              disabled={!editable}
              onChange={(txt) => onCambiar && onCambiar(v.material, 'cobertura', txt)}
            />
            {mixto.cobertura && (
              <Mixto texto={`Sus colores tienen coberturas distintas: ${mixto.cobertura.map((x) => x || '(sin definir)').join(' / ')}. Si eliges una, queda para todos.`} />
            )}
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={varios ? 3 : 4}
              label="Términos legales"
              value={v.legal}
              disabled={!editable}
              placeholder={legalSugerido ? `Vacío = ${legalSugerido}` : 'Términos de este vehículo'}
              onChange={(e) => onCambiar && onCambiar(v.material, 'legal', e.target.value)}
              sx={{ mt: 1.5 }}
            />
            {mixto.legal && <Mixto texto="Sus colores tienen términos distintos. Si escribes unos, quedan para todos." />}
            {editable && !v.legal && legalSugerido && (
              <Button size="small" variant="text" sx={{ mt: 0.5 }} onClick={() => onCambiar && onCambiar(v.material, 'legal', legalSugerido)}>
                Usar el texto sugerido y ajustarlo
              </Button>
            )}
          </div>
          <div style={{ flex: '0 0 220px' }}>
            <div style={{ fontSize: 12, color: '#56606b', marginBottom: 6, fontWeight: 600 }}>Pieza gráfica</div>
            {v.img_url ? (
              <a href={v.img_url} target="_blank" rel="noreferrer" title="Abrir la pieza">
                <img
                  src={v.img_url}
                  alt={`Pieza ${v.etiqueta}`}
                  style={{ width: 210, height: 118, objectFit: 'cover', borderRadius: 6, border: '1px solid #e3e7ec', display: 'block' }}
                />
              </a>
            ) : (
              <div
                style={{
                  width: 210, height: 118, borderRadius: 6, border: '1px dashed #cfd6de', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', color: '#9aa5b1', fontSize: 12.5,
                }}
              >
                Sin pieza
              </div>
            )}
            {mixto.img_url && <Mixto texto="Sus colores tienen piezas distintas." />}
            {editable && (
              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                <BotonArchivo texto={v.img_url ? 'Cambiar' : 'Subir pieza'} variant="text" onArchivo={(f) => onSubirPieza && onSubirPieza(v.material, f)} />
                {v.img_url && (
                  <Button size="small" variant="text" color="error" onClick={() => onCambiar && onCambiar(v.material, 'img_url', '')}>
                    Quitar
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Sin «Para todos los vehículos»: la cobertura se pone en cada vehículo. */}
      {filas.map(tarjeta)}
    </div>
  )
}

export default CoberturaPorMoto
